import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { CloudUpload, Clear } from '@mui/icons-material';
import { DisplayData } from 'src/pages/Solutions/SolutionAssetHierarchy/SolutionAssetHierarchy';
import { ContentType, pepApiRequest, RequestMethod } from 'src/services';
import { API_UPLOAD_DIGITALTWIN_IMAGE } from 'src/pages/Solutions/constants';
import { generateApiPath } from 'src/utils';
import { PlacedProperty } from './TempImagePropertyMapper';
import TempPropertyItem from './TempPropertyItem';
import TempImageCanvas from './TempImageCanvas';
import { PageCardLoader } from '../PageCardLayout';
import { AlertType, useAlert } from '../AlertsProvider';

interface ConfigureTabProps {
  uploadedImage: string | null;
  availableProperties: DisplayData[];
  placedProperties: PlacedProperty[];
  onImageUpload: (imageUrl: string, imageName: string) => void;
  onPropertyVisibilityToggle: (propertyKey: string) => void;
  onPropertyDrop: (propertyKey: string, x: number, y: number) => void;
  onPlacedPropertyRemove: (propertyKey: string) => void;
  onPropertyMove?: (propertyKey: string, x: number, y: number) => void;
  onSaveImageConfiguration?: (
    imageName: string,
    placeProperties: PlacedProperty[]
  ) => void;
  solutionId: string;
  siteId: string;
  twinId: string;
  onClearAll: () => void;
  isDialog?: boolean;
}

const TempConfigureTab: React.FC<ConfigureTabProps> = ({
  uploadedImage,
  availableProperties,
  placedProperties,
  onImageUpload,
  onPropertyVisibilityToggle,
  onPropertyDrop,
  onPlacedPropertyRemove,
  onPropertyMove,
  solutionId,
  siteId,
  twinId,
  onClearAll,
  isDialog = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loader, setLoader] = useState(false);
  const { showAlert } = useAlert();
  const initialSyncDoneRef = useRef(false);

  useEffect(() => {
    if (
      !initialSyncDoneRef.current &&
      availableProperties.length > 0 &&
      placedProperties.length > 0
    ) {
      let hasChanges = false;
      const updatedAvailableProps = [...availableProperties];

      placedProperties.forEach((placedProp) => {
        if (placedProp.key) {
          const availablePropIndex = updatedAvailableProps.findIndex(
            (availProp) => availProp.key === placedProp.key
          );

          if (
            availablePropIndex !== -1 &&
            placedProp.visible === false &&
            updatedAvailableProps[availablePropIndex].visible !== false
          ) {
            onPropertyVisibilityToggle(placedProp.key);
            hasChanges = true;
          }
        }
      });

      initialSyncDoneRef.current = true;
    }
  }, [availableProperties, placedProperties, onPropertyVisibilityToggle]);

  const handleFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setLoader(true);
        pepApiRequest({
          method: RequestMethod.Post,
          url: generateApiPath(API_UPLOAD_DIGITALTWIN_IMAGE, {
            solutionId,
            siteId,
            twinId,
          }),
          contentType: ContentType.FormData,
          params: { image: file },
        }).then(
          (response) => {
            if (response.ok) {
              showAlert({
                type: AlertType.Success,
                title: 'Image File uploaded',
                text: 'Twin image has been uploaded successfully.',
              });
            }
            setLoader(false);
          },
          (err) => {
            showAlert({
              type: AlertType.Error,
              title: 'Image File not uploaded',
              text: 'Error while uploading the image.',
            });
            setLoader(false);
          }
        );
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          onImageUpload(result, file.name);
        };
        reader.readAsDataURL(file);
      }
    },
    [onImageUpload]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const file = event.dataTransfer.files?.[0];
      if (file) {
        const validEvent = {
          target: { files: [file] },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleFileUpload(validEvent);
      }
    },
    [handleFileUpload]
  );
  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    height: '100%',
    minWidth: '900px',
    backgroundColor: '#fff',
    overflow: 'hidden',
  };

  const leftPanelStyle: React.CSSProperties = {
    width: '300px',
    minWidth: '300px',
    backgroundColor: 'white',
    borderRight: '1px solid #e0e0e0',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const rightPanelStyle: React.CSSProperties = {
    flex: 1,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'hidden',
    minWidth: '600px',
  };

  const uploadAreaStyle: React.CSSProperties = {
    border: '2px dashed #1976d2',
    borderRadius: '8px',
    padding: '24px',
    textAlign: 'center',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  };

  const propertiesHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  };

  const propertiesListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const imageHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={leftPanelStyle}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography
            variant="subtitle1"
            style={{ marginBottom: '16px', color: '#333' }}
          >
            Upload Image
          </Typography>
          <Box
            sx={uploadAreaStyle}
            onClick={handleUploadClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <CloudUpload
              style={{ fontSize: 48, color: '#1976d2', marginBottom: '8px' }}
            />
            <Typography variant="body2" style={{ color: '#666' }}>
              Drag and drop your production line image here
            </Typography>
            <Typography
              variant="caption"
              style={{ color: '#999', display: 'block', marginTop: '4px' }}
            >
              or click to browse files
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </Box>

        <Box>
          <Box sx={propertiesHeaderStyle}>
            <Typography variant="subtitle1" style={{ color: '#333' }}>
              Available Properties
            </Typography>
            <Typography variant="caption" style={{ color: '#666' }}>
              {
                availableProperties.filter(
                  (prop) => prop.key !== 'source' && prop.key !== 'type'
                ).length
              }{' '}
              properties
            </Typography>
          </Box>
          <Box sx={propertiesListStyle}>
            {availableProperties
              .filter(
                (property) =>
                  property.key !== 'source' && property.key !== 'type'
              )
              .map((property) => (
                <TempPropertyItem
                  key={property.key}
                  property={property}
                  onVisibilityToggle={onPropertyVisibilityToggle}
                />
              ))}
            <Typography
              variant="caption"
              style={{ color: '#aaa', marginTop: 8 }}
            >
              Drag a property onto the image to position it
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={rightPanelStyle}>
        <Box sx={imageHeaderStyle}>
          <Typography variant="subtitle1" style={{ color: '#333' }}>
            Image Viewer
          </Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography variant="caption" style={{ color: '#666' }}>
              {placedProperties.length} properties positioned
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={onClearAll}
              disabled={placedProperties.length === 0}
            >
              CLEAR ALL
            </Button>
          </Box>
        </Box>

        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          width: '100%', 
          height: '100%',
          minHeight: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}>
          <Box sx={{ 
            width: '100%',
            maxWidth: '1400px', 
            height: 'auto',
            minHeight: '600px',
          }}>
            <TempImageCanvas
              uploadedImage={uploadedImage}
              placedProperties={placedProperties}
              onPropertyDrop={onPropertyDrop}
              onPlacedPropertyRemove={onPlacedPropertyRemove}
              onPropertyMove={onPropertyMove}
              isDragMode={true}
            />
          </Box>
        </Box>
      </Box>
      {loader && <PageCardLoader />}
    </Box>
  );
};

export default TempConfigureTab;
